/**
 * oj-popup.js — one shared overlay component behind every OJ popup section
 * (welcome capture, exit-intent panel, future announcements/gates).
 *
 * Configured per instance via attributes:
 *   name             preview handle → ?preview_popup=<name> force-opens it
 *   trigger          "load" | "exit"
 *   delay            ms before a "load" popup opens
 *   storage-key      localStorage key for suppression
 *   frequency-days   how long a dismissal suppresses re-opening
 *   error-text       message shown when an email capture fails
 *
 * Rules encoded here: CSS owns the animation (JS only toggles [open]);
 * suppression is re-checked on EVERY exit gesture; nothing opens while
 * Shopify's cookie-consent banner is up; an email capture reveals its
 * discount only on a genuine Shopify save (customer_posted=true);
 * everything tears down in disconnectedCallback for the editor.
 */

class OjPopup extends HTMLElement {
  /** Stop watching a banner the visitor is plainly ignoring. */
  static CONSENT_WAIT_MAX = 120000;
  /** Let the banner finish leaving before we arrive. */
  static CONSENT_SETTLE_MS = 450;

  #abort = null;
  #timer = null;
  #watch = null;
  #lastFocus = null;
  #prevOverflow = '';

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const dialog = this.querySelector('[data-oj-popup-dialog]');
    if (!dialog) return;

    // Close: X buttons, the overlay itself, ESC.
    this.querySelectorAll('[data-oj-popup-close]').forEach((el) =>
      el.addEventListener('click', () => this.close(), { signal })
    );
    this.querySelector('[data-oj-popup-overlay]')?.addEventListener(
      'click',
      () => this.close(),
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.#trapFocus(event, dialog);
      },
      { signal }
    );

    this.#bindForm(signal);

    // 1. Preview override — ?preview_popup=<name> opens without persisting.
    const preview = new URLSearchParams(location.search).get('preview_popup');
    if (preview && preview === this.getAttribute('name')) {
      this.open(false);
      return;
    }

    // 2. Editor — open when the merchant selects the section, never on triggers.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener(
        'shopify:section:select',
        (event) => {
          if (event.detail?.sectionId === this.dataset.sectionId) this.open(false);
        },
        { signal }
      );
      document.addEventListener(
        'shopify:section:deselect',
        (event) => {
          if (event.detail?.sectionId === this.dataset.sectionId) this.close();
        },
        { signal }
      );
      return;
    }

    // 3. Live triggers.
    if (this.#suppressed()) return;

    if (this.getAttribute('trigger') === 'exit') {
      document.addEventListener(
        'mouseout',
        (event) => {
          // Re-check every fire — otherwise close → cursor exits → reopens forever.
          if (event.relatedTarget || event.clientY > 0) return;
          if (this.hasAttribute('open') || this.#suppressed()) return;
          // Opening under the consent banner would burn the one exit gesture
          // we get on an invisible panel — skip; the next gesture will land.
          if (OjPopup.consentBannerVisible()) return;
          this.open();
        },
        { signal }
      );
    } else {
      const delay = Number(this.getAttribute('delay')) || 0;
      this.#timer = setTimeout(() => this.#openWhenClear(), delay);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#timer = null;
    this.#stopWatching();
    this.#abort?.abort();
    this.#abort = null;
    if (this.hasAttribute('open')) this.#unlockScroll();
  }

  open(persist = true) {
    if (this.hasAttribute('open')) return;
    this.setAttribute('open', '');
    this.#lastFocus = document.activeElement;
    this.#prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.#focusables()[0]?.focus();
    if (persist) this.#persist();
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.#unlockScroll();
    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
    this.#lastFocus = null;
  }

  #unlockScroll() {
    document.documentElement.style.overflow = this.#prevOverflow;
  }

  #persist() {
    try {
      localStorage.setItem(this.getAttribute('storage-key') || 'oj-popup', String(Date.now()));
    } catch {
      // storage unavailable (private mode) — popup just shows each visit
    }
  }

  #suppressed() {
    try {
      const ts = Number(localStorage.getItem(this.getAttribute('storage-key') || 'oj-popup'));
      if (!ts) return false;
      const days = Number(this.getAttribute('frequency-days')) || 7;
      return Date.now() - ts < days * 86400000;
    } catch {
      return false;
    }
  }

  /**
   * Shopify's cookie-consent banner paints at z-index 2000000 and cannot be
   * out-stacked — nor should it be, the visitor has to be able to answer it.
   * A popup opening underneath is simply lost: on mobile the banner covers the
   * whole form. Note the node is never removed, it flips to display:none, and
   * it is position:fixed — so offsetParent is null even while it is on screen,
   * and its computed visibility stays "visible" after dismissal. Client rects
   * are the honest signal.
   */
  static consentBannerVisible() {
    const banner = document.getElementById('shopify-pc__banner');
    if (!banner || banner.getClientRects().length === 0) return false;
    const styles = getComputedStyle(banner);
    return styles.visibility !== 'hidden' && Number(styles.opacity) > 0;
  }

  /** Open now if nothing is in the way, otherwise as soon as the banner clears. */
  #openWhenClear() {
    if (!OjPopup.consentBannerVisible()) {
      this.open();
      return;
    }

    const started = Date.now();
    const check = () => {
      if (this.hasAttribute('open')) {
        this.#stopWatching();
        return;
      }
      if (OjPopup.consentBannerVisible()) {
        if (Date.now() - started > OjPopup.CONSENT_WAIT_MAX) this.#stopWatching();
        return;
      }
      this.#stopWatching();
      this.#timer = setTimeout(() => this.open(), OjPopup.CONSENT_SETTLE_MS);
    };

    this.#watch = setInterval(check, 400);
    // Consent answered — react on the event rather than wait for the next tick.
    if (this.#abort) {
      document.addEventListener('visitorConsentCollected', check, { signal: this.#abort.signal });
    }
  }

  #stopWatching() {
    clearInterval(this.#watch);
    this.#watch = null;
  }

  #focusables() {
    const dialog = this.querySelector('[data-oj-popup-dialog]');
    return dialog
      ? [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      : [];
  }

  #trapFocus(event, dialog) {
    const focusables = this.#focusables();
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!dialog.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Email capture: reveal the success state ONLY on a genuine Shopify save. */
  #bindForm(signal) {
    const form = this.querySelector('[data-oj-popup-form] form');
    if (!form) return;

    form.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();

        // Editor: show the reveal state without posting anything.
        if (window.Shopify && window.Shopify.designMode) {
          this.#showSuccess();
          return;
        }

        const button = form.querySelector('button[type="submit"]');
        if (button) button.disabled = true;

        let saved = false;
        try {
          const response = await fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'text/html' },
            signal,
          });
          // Shopify redirects to ?customer_posted=true only on a real save.
          saved = response.ok && /customer_posted=true/.test(response.url);
        } catch {
          saved = false;
        }

        if (button) button.disabled = false;

        if (saved) {
          this.#showSuccess();
        } else {
          const error = this.querySelector('[data-popup-error]');
          if (error) {
            error.textContent = this.getAttribute('error-text') || 'Something went wrong — please try again.';
            error.hidden = false;
          }
        }
      },
      { signal }
    );
  }

  #showSuccess() {
    this.querySelector('[data-oj-popup-capture]')?.setAttribute('hidden', '');
    this.querySelector('[data-oj-popup-reveal]')?.removeAttribute('hidden');
    const error = this.querySelector('[data-popup-error]');
    if (error) error.hidden = true;
    this.#focusables()[0]?.focus();
  }
}

if (!customElements.get('oj-popup')) {
  customElements.define('oj-popup', OjPopup);
}

/** Copy-to-clipboard for discount codes shown inside popups. */
class OjPopupCopy extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const button = this.querySelector('button');
    button?.addEventListener(
      'click',
      async () => {
        try {
          await navigator.clipboard.writeText(this.dataset.code || '');
          const label = button.querySelector('[data-oj-copy-label]') || button;
          const original = label.textContent;
          label.textContent = this.dataset.copiedText || 'Copied';
          setTimeout(() => {
            label.textContent = original;
          }, 2000);
        } catch {
          // clipboard unavailable — the code is visible to copy by hand
        }
      },
      { signal: this.#abort.signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }
}

if (!customElements.get('oj-popup-copy')) {
  customElements.define('oj-popup-copy', OjPopupCopy);
}
